<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    loading-message="Loading Mailings..."
    button-location="top"
    @submit="getMailings()"
  >
    <template #title>
      Mailing Search
    </template>

    <template #top-form>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Search Type*</label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="searchCondition.type"
            :options="queryTypeList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Mailing Type*</label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="searchCondition.mailingType"
            :options="searchableMailingTypeList"
          />
        </div>
      </div>

      <div class="row align-items-center customer-phone">
        <label class="col-sm-3 control-label">Search Value*</label>
        <div
          v-if="isSearchByAddress"
          class="col-sm-5"
        >
          <address-autocomplete-input
            id="mailingAddress"
            address-type="Full Address"
            :anchor-bound="anchorBound"
            @place-changed="setMailingAddresses"
          />
        </div>

        <div
          v-else
          class="col-sm-5"
        >
          <sanitized-input
            v-model="searchCondition.query"
            placeholder="Please input search value"
            required
          />
        </div>
      </div>

      <div class="row">
        <label class="col-sm-3 control-label pt-10"> Time Span </label>
        <div class="col-sm-5">
          <date-picker
            v-model="timeSpanValue"
            type="daterange"
          >
            <template #comment>
              <span class="help-block"> Please click the same day twice if you only want results from that particular day </span>
            </template>
          </date-picker>
        </div>

        <div class="col-sm-3 col-12 left-button-group form-submit-button-group pt-5 mobile-pt-0">
          <p-button
            type="purple"
            round
            native-type="submit"
            :disabled="status.isLoading"
          >
            Search
          </p-button>

          <p-button
            type="yellow"
            round
            :disabled="status.isLoading"
            @click="resetForm"
          >
            Reset
          </p-button>
        </div>
      </div>

      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="status.currentPage"
          :total-pages="status.totalPages"
          @load-page-data="getMailings"
        />
      </div>
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="mailings"
        :table-columns="tableColumns"
      />
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, watch, computed, onMounted } from 'vue';
import { startCase } from 'lodash';
import Moment from 'moment-timezone';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { CustomLocationType } from '@/models/address';
import { ListType } from '@/models/app';
import { TableColumnType } from '@/models/table';
import { Mailing } from '@/models/marketing';
import { marketingService } from '@/services/marketing';

const pageId = 'mailing-search';
const paginationLimit = 30;
const tableColumns: TableColumnType[] = [
  { label: 'Requested At', field: 'createdAt', width: 180, isDateField: true },
  { label: 'Sent At', field: 'sentAt', width: 180, isDateField: true },
  { label: 'ID', field: 'id', width: 280 },
  { label: 'Name', field: 'name', width: 140 },
  { label: 'Phone', field: 'phoneNumber', width: 140 },
  { label: 'Caller ID', field: 'userId', width: 280 },
  { label: 'Address', field: 'address', width: 140 },
  { label: 'Type', field: 'type', width: 160 },
  { label: 'Document', field: 'document', width: 100 },
];
const queryTypeList: ListType[] = [
  { id: 1, key: 'phone-number', label: 'Phone Number' },
  { id: 2, key: 'address', label: 'Address' },
];
const mailingTypeList: ListType[] = [
  { id: 1, key: 'reactivation-postcards', label: 'Reactivation Postcards' },
  { id: 2, key: 'happy-birthday-postcards', label: 'Happy Birthday Postcards' },
  { id: 3, key: 'monthly-invoice', label: 'Monthly Invoice' },
  { id: 4, key: 'postcard-recipients-follow-ups', label: 'Postcard Recipients Follow Ups' },
  { id: 5, key: 'gourmet-restaurants', label: 'Gourmet Restaurants' },
  {
    id: 6,
    key: 'brochure',
    label: 'Brochure',
    children: [
      { id: 61, key: 'regular-brochure', label: 'Regular Brochure' },
      { id: 62, key: 'regular-brochure-v2', label: 'Regular Brochure V2' },
      { id: 63, key: 'regular-brochure-v3', label: 'Regular Brochure V3' },
      { id: 64, key: 'gold-brochure', label: 'Gold Brochure' },
      { id: 65, key: 'spanish-regular-brochure', label: 'Spanish Regular Brochure' },
      { id: 66, key: 'regular-brochure-pc', label: 'Regular Brochure to Potential Customer' },
      { id: 67, key: 'regular-brochure-lo', label: 'Regular Brochure to Loved One' },
      { id: 68, key: 'regular-brochure-co', label: 'Regular Brochure to Business' },
    ],
  },
  {
    id: 10,
    key: 'welcome-kit',
    label: 'Welcome Kit',
    children: [
      { id: 11, key: 'welcome-kit', label: 'Welcome Kit' },
      { id: 12, key: 'welcome-kit-spanish', label: 'Spanish Welcome Kit' },
      { id: 13, key: 'welcome-kit-kelly', label: 'Welcome Kit Kelly' },
      { id: 14, key: 'welcome-kit-deandre', label: 'Welcome Kit DeAndre' },
      { id: 15, key: 'welcome-kit-gourmet-english', label: 'Welcome Kit Gourmet' },
    ],
  },
  { id: 16, key: 'bio-bank-consent-form', label: 'BioBank Consent Form' },
  { id: 17, key: 'flu-study-consent-form', label: 'Flu Study Consent Form' },
  { id: 18, key: 'facebook-brochure', label: 'Facebook Brochure' },
];

const { showErrorPopupDialog } = useSwalDialog();
const { customerPhoneNumber, anchorBound } = useCustomer();

const mailings: Ref<Mailing[]> = ref([]);
const mailingAddress: Ref<CustomLocationType | null> = ref(null);
const searchCondition: Ref<any> = ref({
  type: '',
  query: '',
  startDate: '',
  endDate: '',
  mailingType: '',
});
const status = reactive({
  isLoading: false,
  currentPage: 1,
  totalPages: 1,
});
const timeSpanValue: Ref<any> = ref(null);

const isSearchByAddress = computed(() => searchCondition.value.type === 'address');

/*
  We need to make computed variable to filter only parent items.
  B/c we need to find mailings by main type like brochure, welcome kit.
  If we use original list, the dropdown will have subgroup and it allow us to search by individual type like regular-brochure, gold-brochure, etc.
*/
const searchableMailingTypeList = computed(() =>
  mailingTypeList.map((item: ListType) => ({
    id: item.id,
    key: item.key,
    label: item.label,
  })),
);

const setMailingAddresses = (address: CustomLocationType | null) => {
  mailingAddress.value = {
    streetAddress: address?.streetAddress || '',
    city: address?.city || '',
    state: address?.state || '',
    postalCode: address?.zip || '',
    zip: address?.zip || '',
    country: address?.country || '',
  };
};

const getMailings = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * paginationLimit;

  if (isSearchByAddress.value && !mailingAddress.value?.streetAddress) {
    showErrorPopupDialog({
      text: 'You must select the address from the dropdown',
    });
    return;
  }

  status.currentPage = currentPage;
  status.isLoading = true;

  try {
    let mailingTypes = [searchCondition.value.mailingType];

    const mailingTypeItem = mailingTypeList.find((item) => item.key === searchCondition.value.mailingType);
    if (mailingTypeItem?.children) {
      mailingTypes = mailingTypeItem.children.map((item) => item.key);
    }

    const response = await marketingService.getMailings({
      searchType: searchCondition.value.type,
      phoneNumber: searchCondition.value.query,
      address: {
        streetAddress: mailingAddress.value?.streetAddress || '',
        city: mailingAddress.value?.city || '',
        state: mailingAddress.value?.state || '',
        postalCode: mailingAddress.value?.zip || '',
        country: mailingAddress.value?.country || '',
      },
      mailingTypes,
      startDate: searchCondition.value.startDate,
      endDate: searchCondition.value.endDate,
      pagination: {
        limit: paginationLimit,
        offset,
      },
    });

    status.totalPages = Math.ceil(response.count / paginationLimit) || 1;

    mailings.value = response.rows.map((item: Mailing) => {
      const documentUrl = item.sendResult?.status === 'success' && item.sendResult?.result.url ? item.sendResult.result.url : '';

      // make mailing object to show them on the table
      return {
        id: item.id,
        name: util.getFullName(item.firstName, item.lastName),
        phoneNumber: item.phoneNumber,
        createdAt: item.createdAt,
        sentAt: item.sentAt,
        address: item.streetAddress,
        type: startCase(item.type ?? ''),
        userId: item.userId,
        document: documentUrl ? `<a href="${documentUrl}" target="_blank">Link</a>` : 'No Link',
        updatedAt: item.updatedAt,
      };
    });
  } catch (error) {
    mailings.value = [];
    status.currentPage = 1;
    status.totalPages = 1;

    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const resetForm = () => {
  status.currentPage = 1;
  status.totalPages = 1;
  mailings.value = [];
  mailingAddress.value = null;
  timeSpanValue.value = null;
  searchCondition.value.type = queryTypeList[0].key;
  searchCondition.value.query = '';
  searchCondition.value.mailingType = mailingTypeList[0].key;
};

watch(timeSpanValue, (value: any) => {
  if (value) {
    searchCondition.value.startDate = util.formatDateTime(value[0], 'YYYY-MM-DD HH:mm:ss');
    searchCondition.value.endDate = util.formatDateTime(Moment(value[1]).endOf('day'), 'YYYY-MM-DD HH:mm:ss');
  } else {
    searchCondition.value.startDate = '';
    searchCondition.value.endDate = '';
  }
});

watch(
  () => searchCondition.value.type,
  (value: string, oldValue: string) => {
    if (oldValue) {
      searchCondition.value.query = '';
      mailingAddress.value = null;
    }
  },
);

onMounted(() => {
  util.moveToTop();
  searchCondition.value.type = queryTypeList[0].key;
  searchCondition.value.query = customerPhoneNumber.value;
  searchCondition.value.mailingType = mailingTypeList[0].key;

  if (searchCondition.value.query) {
    getMailings();
  }
});
</script>
